import type { ContentPreviewData } from '../types';

export interface UtmParameters {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}

export interface CustomUtmParameter {
  key: string;
  value: string;
}

export const DYNAMIC_UTM_OPTIONS = {
  '{{ad.name}}': 'Ad Name',
  '{{adset.name}}': 'Ad Set Name',
  '{{creator.name}}': 'Creator Name',
  '{{creator.handle}}': 'Creator Handle',
  '{{creator.ig_username}}': 'Creator Instagram Username (no @)',
  '{{project.name}}': 'Project Name',
  '{{post.id}}': 'Post ID',
  '{{platform}}': 'Platform',
  '{{member.id}}': 'Member ID',
};

export const UTM_FIELD_OPTIONS = ['Dynamic Parameters', ...Object.keys(DYNAMIC_UTM_OPTIONS)];

/**
 * Additional context for resolving dynamic parameters
 */
export interface DynamicParameterContext {
  adName?: string;
  adsetName?: string;
}

/**
 * Builds a complete URL with UTM parameters appended
 */
export function buildUrlWithUtm(
  baseUrl: string,
  utmParameters?: UtmParameters,
  customParameters?: CustomUtmParameter[],
): string {
  if (!utmParameters && !customParameters?.length) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl);

    // Add standard UTM parameters
    if (utmParameters) {
      if (utmParameters.utmSource) url.searchParams.set('utm_source', utmParameters.utmSource);
      if (utmParameters.utmMedium) url.searchParams.set('utm_medium', utmParameters.utmMedium);
      if (utmParameters.utmCampaign) url.searchParams.set('utm_campaign', utmParameters.utmCampaign);
      if (utmParameters.utmContent) url.searchParams.set('utm_content', utmParameters.utmContent);
      if (utmParameters.utmTerm) url.searchParams.set('utm_term', utmParameters.utmTerm);
    }

    // Add custom parameters
    if (customParameters?.length) {
      customParameters.forEach((param) => {
        if (param.key && param.value) {
          url.searchParams.set(param.key, param.value);
        }
      });
    }

    return url.toString();
  } catch (error) {
    // Log the error for debugging purposes
    console.error('Invalid URL provided to buildUrlWithUtm:', baseUrl, error);

    // Return the original baseUrl as fallback
    return baseUrl;
  }
}

/**
 * Resolves dynamic parameters with actual values from content data
 */
export function resolveDynamicParameter(
  parameter: string,
  content: ContentPreviewData,
  context?: DynamicParameterContext,
): string {
  const dynamicValues: Record<string, string> = {
    '{{ad.name}}': context?.adName || '',
    '{{adset.name}}': context?.adsetName || '',
    '{{creator.name}}': content.creator?.name || '',
    '{{creator.handle}}': content.creator?.handle || '',
    '{{creator.ig_username}}': content.creator?.handle?.replace('@', '') || '',
    '{{project.name}}': content.metadata?.project?.name || '',
    '{{post.id}}': content.postExternalId || '',
    '{{platform}}': content.metadata?.platform || '',
    '{{member.id}}': content.creator?.id?.toString() || '',
  };

  return dynamicValues[parameter] || parameter;
}

/**
 * Resolves all dynamic parameters in UTM values
 */
export function resolveUtmParameters(
  utmParameters: UtmParameters,
  customParameters: CustomUtmParameter[],
  content: ContentPreviewData,
  context?: DynamicParameterContext,
): { utmParameters: UtmParameters; customParameters: CustomUtmParameter[] } {
  const resolvedUtm: UtmParameters = {};

  if (utmParameters.utmSource) {
    resolvedUtm.utmSource = resolveDynamicParameter(utmParameters.utmSource, content, context);
  }
  if (utmParameters.utmMedium) {
    resolvedUtm.utmMedium = resolveDynamicParameter(utmParameters.utmMedium, content, context);
  }
  if (utmParameters.utmCampaign) {
    resolvedUtm.utmCampaign = resolveDynamicParameter(utmParameters.utmCampaign, content, context);
  }
  if (utmParameters.utmContent) {
    resolvedUtm.utmContent = resolveDynamicParameter(utmParameters.utmContent, content, context);
  }
  if (utmParameters.utmTerm) {
    resolvedUtm.utmTerm = resolveDynamicParameter(utmParameters.utmTerm, content, context);
  }

  const resolvedCustom = customParameters.map((param) => ({
    key: resolveDynamicParameter(param.key, content, context),
    value: resolveDynamicParameter(param.value, content, context),
  }));

  return {
    utmParameters: resolvedUtm,
    customParameters: resolvedCustom,
  };
}

/**
 * Validates UTM parameter value
 */
export function validateUtmValue(value: string): string | null {
  if (value.includes('#') || value.includes('=') || value.includes(' ')) {
    return "Value cannot contain '#', '=' or space characters";
  }
  return null;
}
